// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadWishLists from "@insite/client-framework/Store/Pages/MyLists/Handlers/LoadWishLists";
import toggleFiltersOpen from "@insite/client-framework/Store/Pages/MyLists/Handlers/ToggleFiltersOpen";
import updateLoadParameter from "@insite/client-framework/Store/Pages/MyLists/Handlers/UpdateLoadParameter";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonIcon, ButtonIconPresentationProps, ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import ChevronsUpDown from "@insite/mobius/Icons/ChevronsUpDown";
import Filter from "@insite/mobius/Icons/Filter";
import OverflowMenu, { OverflowMenuProps } from "@insite/mobius/OverflowMenu";
import Select, { SelectProps } from "@insite/mobius/Select";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    return {
        query: state.pages.myLists.getWishListsParameter.query,
        currentSort: state.pages.myLists.getWishListsParameter.sort,
    };
};

const mapDispatchToProps = {
    updateLoadParameter,
    loadWishLists,
    toggleFiltersOpen,
};

type Props = WidgetProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface MyListsSortControlStyles {
    wrapper?: InjectableCss;
    sortByOverflowMenu?: OverflowMenuProps;
    sortByClickable?: ClickablePresentationProps;
    selectedSortByClickable?: ClickablePresentationProps;
    toggleFilterButton?: ButtonPresentationProps;
    toggleFilterButtonIcon?: ButtonIconPresentationProps;
    // Xng-Change
    select?: SelectProps;
}

export const sortControlStyles: MyListsSortControlStyles = {
    wrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
        `,
    },
    sortByOverflowMenu: {
        iconProps: { src: ChevronsUpDown },
    },
    selectedSortByClickable: {
        css: css`
            color: ${getColor("primary")};
        `,
    },
    toggleFilterButton: {
        color: "common.background",
        shape: "pill",
        buttonType: "solid",
        shadow: false,
        css: css`
            width: 40px;
            padding: 0;
            margin-right: 10px;
        `,
    },
    toggleFilterButtonIcon: {
        src: Filter,
        size: 24,
    },
};

const styles = sortControlStyles;

const sortOptions = [
    { value: "ModifiedOn DESC", displayName: translate("Last Updated") },
    { value: "Name ASC", displayName: translate("List Name: A-Z") },
    { value: "Name DESC", displayName: translate("List Name: Z-A") },
];

const MyListsSortControl = ({ currentSort, toggleFiltersOpen, updateLoadParameter }: Props) => {
    // Xng-Change
    // use event instead of value parameter
    const sortChangeHandler = (event: React.FormEvent<HTMLSelectElement>) => {
        updateLoadParameter({ sort: event?.currentTarget.value });
    };

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="myListPage_productsSortBy">
            {/* Xng-Change */}
            {/* Move toggleFiltersOpen into MyListsFilter widget */}
            {/* and use select dropdown as in the previous version we have in SB */}
            {/* <Button
                {...styles.de }
                onClick={() => toggleFiltersOpen()}
                aria-label={translate("Toggle Filter")}
                data-test-selector="mylists_toggleFilter"
            >
                <ButtonIcon {...styles.toggleFilterButtonIcon} />
            </Button> */}
            {/* <OverflowMenu {...styles.sortByOverflowMenu}>
                {sortOptions?.map(sortOption => (
                    <Clickable
                        {...(sortOption.value === currentSort
                            ? styles.selectedSortByClickable
                            : styles.sortByClickable)}
                        key={sortOption.value}
                        onClick={() => sortChangeHandler(sortOption.value)}
                    >
                        {sortOption.displayName}
                    </Clickable>
                ))}
            </OverflowMenu> */}
            <Select
                data-test-selector="myLists_sort"
                {...styles.select}
                onChange={(event: React.FormEvent<HTMLSelectElement>) => sortChangeHandler(event)}>
                <option value="ModifiedOn DESC">{translate("Last Updated")}</option>
                <option value="Name ASC">{translate("List Name: A-Z")}</option>
                <option value="Name DESC">{translate("List Name: Z-A")}</option>
            </Select>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(MyListsSortControl),
    definition: {
        group: "My Lists",
        displayName: "Sort Control",
        allowedContexts: ["MyListsPage"],
    },
};

export default widgetModule;
